// i18next-extract-mark-ns-start pay-by-link

//Payment methods
import {AnchorLink} from 'components/AnchorLink';
import {Background} from 'components/Background';
import {Clients} from 'components/Clients';
import {ContactSalesButton} from 'components/ContactSalesButton';
import {Content} from 'components/Content';
import {FaqsSection, getFaqSchema} from 'components/FaqsSection';
import {List} from 'components/List';
import {Section, SectionActions, SectionHeader, SectionImage} from 'components/Section';
import {SEO} from 'components/SEO';
import {SignUpButton} from 'components/SignUpButton';
import {graphql, PageProps} from 'gatsby';
import {Trans, useI18next} from 'gatsby-plugin-react-i18next';
import {getDocsUrl} from 'utils/docsLinks';

//Images
import heroImage from 'images/pay-by-link-preview-atelier.png';
import heroImageEs from 'images/pay-by-link-preview-es.png';
import howItWorksAnimation from 'images/pay-by-link-atelier-animation.mp4';
import howItWorksAnimationEs from 'images/pay-by-link-atelier-animation-es.mp4';

// Clients
import index1 from 'images/payment_gateway.svg';
import index6 from 'images/payment_provider.svg';
import index3 from 'images/shopify_payment_gateway.svg';

import React from 'react';
import styled from 'styled-components';
import {BlogLink} from 'components/links/Blog';
import {InternalPageLink} from 'components/links/Pages';
import {GoogleReviews, Place} from 'components/GoogleReviews';
import {ScreenSizes} from '../../../types/responsive';

const IndexBackground = styled.div`
  position: relative;

  &:before {
    display: block;
    content: '';
    position: absolute;
    transform: rotate(-58deg);
    left: 70%;
    top: 0;
    height: 770px;
    width: 1150px;
    z-index: -1;
    background: #7984d9;
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const IndexContent = styled.div`
  padding-top: 30px;
  max-width: 500px;
  @media (max-width: 768px) {
    padding-top: 10px;
  }
`;

const RightCol = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 40px;

  @media (max-width: 1024px) {
    padding-left: 24px;
  }
  @media (max-width: 768px) {
    padding-left: 0;
    margin-top: 20px;
    display: none;
  }
}
`;

const HeroImage = styled.img`
  max-width: 100%;
  height: auto;
  max-height: 660px;
  border-radius: 8px;
  box-shadow:
    0 4px 6px rgba(50, 50, 93, 0.11),
    0 1px 3px rgba(0, 0, 0, 0.08);
`;

const HowItWorksGrid = styled.div`
  display: grid;
  width: 100%;
  column-gap: 60px;
  align-items: start;
  grid-template-columns: minmax(280px, 340px) 1fr;
  grid-template-areas:
    'video header'
    'video intro'
    'video steps'
    'video actions';

  @media (max-width: ${ScreenSizes.sm}) {
    grid-template-columns: 1fr;
    row-gap: 24px;
    grid-template-areas:
      'header'
      'intro'
      'video'
      'steps'
      'actions';
  }
`;

const HowItWorksVideoWrapper = styled.div`
  grid-area: video;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HowItWorksVideo = styled.video`
  align-self: center;
  width: 100%;
  max-width: 320px;
  height: auto;
  border-radius: 24px;
  box-shadow:
    0 4px 6px rgba(50, 50, 93, 0.11),
    0 1px 3px rgba(0, 0, 0, 0.08);
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 600px;
  margin: 40px 0 0;
`;

const StepRow = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  padding-bottom: 40px;
  &:last-child {
    padding-bottom: 0;
  }
`;

const StepDottedLine = styled.div`
  position: absolute;
  left: 19px;
  top: 40px;
  bottom: 0;
  width: 1px;
  border-left: 1px dashed #78d9cf;
`;

const StepCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 40px;
  min-width: 40px;
  border-radius: 50%;
  background-color: #78d9cf;
  z-index: 1;
  margin-right: 24px;
`;

const StepNum = styled.p`
  font-weight: 600;
  color: white;
  font-size: 20px;
  margin-bottom: 0;
`;

const StepText = styled.div`
  padding-top: 4px;
  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 6px;
  }
  p {
    font-size: 0.95rem;
    margin-bottom: 0;
  }
`;

const CenteredIntro = styled.div`
  text-align: center;
  max-width: 780px;
  margin: 0 auto 24px;
`;

const ReviewsBackground = styled.div`
  position: relative;
  margin-top: 100px;

  &:before {
    display: block;
    content: '';
    z-index: -1;
    height: 70%;
    position: absolute;
    transform: skew(0, -9deg);
    left: 0;
    right: 0;
    bottom: 0;
    top: 20%;
    background: linear-gradient(96.34deg, #00e9d5 0%, #7984d9 100%);
  }
`;

const PayByLink: React.FC<PageProps<any>> = (props) => {
  const {t, i18n, language} = useI18next();
  const isSpanish = i18n.language === 'es';
  const currentHeroImage = isSpanish ? heroImageEs : heroImage;
  const currentHowItWorksAnimation = isSpanish ? howItWorksAnimationEs : howItWorksAnimation;
  const data = props.data as any;
  const place: Place = {
    rating: data.googlePlacesPlace.rating,
    user_ratings_total: data.googlePlacesPlace.user_ratings_total,
    reviews: data.allGooglePlacesReview.nodes
  };
  const faqs = [
    {
      header: t('What is Pay By Link?'),
      text: t(
        'Pay By Link is a MONEI feature that lets you create secure payment links and send them to your customers by email, SMS, WhatsApp, social media, or any other channel. Customers just click the link and pay online in one click, no website or checkout page of your own required.'
      ),
      content: t(
        'Pay By Link is a MONEI feature that lets you create secure payment links and send them to your customers by email, SMS, WhatsApp, social media, or any other channel. Customers just click the link and pay online in one click, no website or checkout page of your own required.'
      )
    },
    {
      header: t('Can I use Pay By Link without a website?'),
      text: t(
        'Yes, it\'s possible to send payment links using Pay By Link without a website via WhatsApp, social media, or email. If you don\'t have a website, while creating your MONEI account you must select "Physical store" when filling out the business details form. If you select "E-commerce, online store", you will be required to provide a website URL to complete your account setup.'
      ),
      content: t(
        'Yes, it\'s possible to send payment links using Pay By Link without a website via WhatsApp, social media, or email. If you don\'t have a website, while creating your MONEI account you must select "Physical store" when filling out the business details form. If you select "E-commerce, online store", you will be required to provide a website URL to complete your account setup.'
      )
    },
    {
      header: t('Can I set a limit date for the payment?'),
      text: t(
        'Yes. By default, the payment link is active for 7 days from creation, but you can set up a date to your convenience.'
      ),
      content: t(
        'Yes. By default, the payment link is active for 7 days from creation, but you can set up a date to your convenience.'
      )
    },
    {
      header: t('What other information can I add to the payment link form?'),
      text: t(
        'The amount and allowed payment methods are required by default, but you can also add other information such as customer email and billing address (required for Multibanco and MB Way payments, otherwise optional), expiration date, order ID, customer details, description, store, and shipping address.'
      ),
      content: t(
        'The amount and allowed payment methods are required by default, but you can also add other information such as customer email and billing address (required for Multibanco and MB Way payments, otherwise optional), expiration date, order ID, customer details, description, store, and shipping address.'
      )
    },
    {
      header: t('Can I customize the payment page?'),
      text: t(
        'Yes. You can add your logo and brand colors. Go to your Branding settings in your MONEI dashboard to personalize your payment page.'
      ),
      content: t(
        'Yes. You can add your logo and brand colors. Go to your Branding settings in your MONEI dashboard to personalize your payment page.'
      )
    }
  ];
  const schemaFaqs = getFaqSchema(faqs);
  return (
    <>
      <SEO
        path="pay-by-link"
        title={t('Pay By Link')}
        description={t(
          'Use Pay By Link to send your customers a link to pay online in one click! Works with or without a website. Send payment links via email or SMS. Start now >>'
        )}
        schema={schemaFaqs}
      />
      <IndexBackground>
        <Content>
          <Section reverseOnMobile>
            <IndexContent>
              <SectionHeader underline tagName="h1">
                <Trans>Create Pay By Link requests with MONEI</Trans>
              </SectionHeader>
              <Trans parent="p">
                Pay By Link lets you create a unique, secure payment link that your customers can
                use to pay online in one click. Stop spending time on manual invoices and use
                MONEI's Pay By Link feature to generate payment links hosted by us and set up by you
                in seconds. This flexible way to sell lets you process online transactions quickly
                and securely, with or without a website.
              </Trans>
              <SectionActions>
                <SignUpButton variant="light" style={{marginRight: 15}}>
                  <Trans>Open an Account</Trans>
                </SignUpButton>
                <ContactSalesButton />
              </SectionActions>
            </IndexContent>
            <RightCol>
              <HeroImage src={currentHeroImage} alt="MONEI Pay By Link payment page preview" />
            </RightCol>
          </Section>
        </Content>
      </IndexBackground>
      <Background>
        <Content>
          <Section>
            <HowItWorksGrid>
              <SectionHeader style={{gridArea: 'header'}}>
                <Trans>How does Pay By Link work?</Trans>
              </SectionHeader>
              <Trans parent="p" style={{gridArea: 'intro'}}>
                Payment links give you total flexibility over how and when you request payments from
                your customers. Create your payment page and then send the link via your preferred
                channel including email, SMS, social media,{' '}
                <InternalPageLink slug="features/whatsapp-payments">WhatsApp</InternalPageLink>,
                etc.
              </Trans>
              <HowItWorksVideoWrapper>
                <HowItWorksVideo key={i18n.language} autoPlay loop muted playsInline>
                  <source src={currentHowItWorksAnimation} type="video/mp4" />
                </HowItWorksVideo>
              </HowItWorksVideoWrapper>
              <StepsContainer style={{gridArea: 'steps'}}>
                <StepRow>
                  <StepCircle>
                    <StepNum>1</StepNum>
                  </StepCircle>
                  <StepText>
                    <h3>
                      <Trans>
                        Create your MONEI account (as a physical store if you don't have a website)
                      </Trans>
                    </h3>
                    <p>
                      <Trans>
                        Choose your preferred payment methods and personalize your payment page.
                      </Trans>
                    </p>
                  </StepText>
                  <StepDottedLine />
                </StepRow>
                <StepRow>
                  <StepCircle>
                    <StepNum>2</StepNum>
                  </StepCircle>
                  <StepText>
                    <h3>
                      <Trans>Generate your payment link in less than a minute</Trans>
                    </h3>
                    <p>
                      <Trans>
                        Enter the required information, including the amount and allowed payment
                        methods.
                      </Trans>
                    </p>
                  </StepText>
                  <StepDottedLine />
                </StepRow>
                <StepRow>
                  <StepCircle>
                    <StepNum>3</StepNum>
                  </StepCircle>
                  <StepText>
                    <h3>
                      <Trans>Share the link with your client via your chosen platform</Trans>
                    </h3>
                    <p>
                      <Trans>
                        Email, SMS, social media, digital invoice, QR code, WhatsApp, and more.
                      </Trans>
                    </p>
                  </StepText>
                  <StepDottedLine />
                </StepRow>
                <StepRow>
                  <StepCircle>
                    <StepNum>4</StepNum>
                  </StepCircle>
                  <StepText>
                    <h3>
                      <Trans>
                        Your client receives a payment notification and completes the transaction
                      </Trans>
                    </h3>
                  </StepText>
                  <StepDottedLine />
                </StepRow>
                <StepRow>
                  <StepCircle>
                    <StepNum>5</StepNum>
                  </StepCircle>
                  <StepText>
                    <h3>
                      <Trans>You and your client receive the confirmation message</Trans>
                    </h3>
                    <p>
                      <Trans>Manage your payments anytime from MONEI's real-time dashboard.</Trans>
                    </p>
                  </StepText>
                </StepRow>
              </StepsContainer>
              <SectionActions style={{gridArea: 'actions'}}>
                <SignUpButton variant="dark" style={{marginRight: 15}}>
                  <Trans>Discover MONEI</Trans>
                </SignUpButton>
              </SectionActions>
            </HowItWorksGrid>
          </Section>
          <Section reverseOnMobile>
            <div>
              <SectionHeader>
                <Trans>Create your payment links in a secure environment</Trans>
              </SectionHeader>
              <Trans parent="p">
                Leave <InternalPageLink slug="payment-security">payment security</InternalPageLink>{' '}
                up to us. We host your Pay By Link page and safely process transactions in
                compliance with{' '}
                <BlogLink slug="what-is-3d-secure-and-its-advantages-for-e-commerce">
                  3D Secure
                </BlogLink>{' '}
                and <BlogLink slug="understanding-pci-compliance">Level 1 PCI DSS</BlogLink>{' '}
                regulations. After you send a payment request, track the transaction status in
                real-time from your MONEI dashboard. You’ll receive an automated notification once
                the customer has paid.
              </Trans>
              <SectionActions>
                <SignUpButton variant="dark" style={{marginRight: 15}}>
                  <Trans>Get MONEI Now</Trans>
                </SignUpButton>
              </SectionActions>
            </div>
            <SectionImage
              src={index3}
              alt="Secure payment gateway"
              title="Secure payment gateway"
              width={400}
              height={428}
              mobileWidth={180}
            />
          </Section>
          <Section>
            <SectionImage
              src={index1}
              alt="Secure payment gateway"
              title="Secure payment gateway"
              width={600}
              height={600}
              mobileWidth={280}
            />
            <div>
              <SectionHeader>
                <Trans>Accept a wide range of payment methods</Trans>
              </SectionHeader>
              <Trans parent="p">
                Accepting more payment methods leads to improved customer satisfaction and more
                sales. That’s why we aggregate{' '}
                <InternalPageLink slug="payment-methods">
                  all major and alternative payment methods
                </InternalPageLink>{' '}
                in a single platform. We aim to help you reach more customers and grow your business
                faster.
              </Trans>
              <Trans parent="p">
                Use Pay By Link to{' '}
                <InternalPageLink slug="payment-methods/credit-cards">
                  accept credit cards
                </InternalPageLink>
                , digital wallets including{' '}
                <InternalPageLink slug="payment-methods/google-pay">Google Pay</InternalPageLink>,{' '}
                <InternalPageLink slug="payment-methods/apple-pay">Apple Pay</InternalPageLink>, and{' '}
                <InternalPageLink slug="payment-methods/paypal">PayPal</InternalPageLink>, or local
                payment methods such as{' '}
                <InternalPageLink slug="payment-methods/bizum">Bizum</InternalPageLink> in Spain,{' '}
                <InternalPageLink slug="payment-methods/multibanco">Multibanco</InternalPageLink>{' '}
                and <InternalPageLink slug="payment-methods/mb-way">MB Way</InternalPageLink> in
                Portugal, etc.
              </Trans>
              <Trans parent="p">
                Learn how to{' '}
                <AnchorLink href={getDocsUrl('360003378957', language)}>
                  configure payment methods in your MONEI account
                </AnchorLink>
                .
              </Trans>
              <SectionActions>
                <SignUpButton variant="dark" style={{marginRight: 15}}>
                  <Trans>Open an Account</Trans>
                </SignUpButton>
              </SectionActions>
            </div>
          </Section>
          <Section reverseOnMobile>
            <div>
              <SectionHeader>
                <Trans>Easily create your customized payment page</Trans>
              </SectionHeader>
              <Trans parent="p">
                Add your logo and brand colors to customize your payment page and build brand
                awareness. If you already have a website, you can also{' '}
                <AnchorLink href={getDocsUrl('360017953818', language)}>
                  add your custom domain
                </AnchorLink>{' '}
                to the payment link.
              </Trans>
              <Trans parent="p">
                Don't have a website?{' '}
                <AnchorLink href="https://dashboard.monei.com/?action=signUp">
                  Register with MONEI
                </AnchorLink>{' '}
                as a physical business and start using Pay By Link!
              </Trans>
              <SectionActions>
                <SignUpButton variant="dark" style={{marginRight: 15}}>
                  <Trans>Open an Account</Trans>
                </SignUpButton>
              </SectionActions>
            </div>
            <SectionImage
              src={index6}
              alt="Payment Gateway for Developers"
              title="Payment Gateway for Developers"
              width={392}
              height={358}
            />
          </Section>
        </Content>
      </Background>
      <Content>
        <Section>
          <div>
            <CenteredIntro>
              <SectionHeader underline sx={{textAlign: 'center'}}>
                <Trans>Who is Pay By Link for?</Trans>
              </SectionHeader>
              <Trans parent="p">
                Any business or freelancer without a website, or that needs to request payments
                outside of their existing online or in-person payment setup.
              </Trans>
              <Trans parent="p">
                <strong>The applications of payment links are endless!</strong>
              </Trans>
              <Trans parent="p">
                Use email, social media,{' '}
                <InternalPageLink slug="features/whatsapp-payments">WhatsApp</InternalPageLink>,
                digital invoices, SMS,{' '}
                <InternalPageLink slug="monei-pay/qr">QR codes</InternalPageLink> and more to reach
                your clients where they are and improve your payment flexibility, via a secure
                platform.
              </Trans>
            </CenteredIntro>
            <List style={{textAlign: 'center', listStylePosition: 'inside', marginLeft: 0}}>
              <Trans parent="li">
                Service-based businesses (cleaners, personal trainers,{' '}
                <InternalPageLink slug="payment-gateway-elearning">teachers</InternalPageLink> or{' '}
                <InternalPageLink slug="monei-pay/pos-for-freelancers">
                  freelancers
                </InternalPageLink>
                )
              </Trans>
              <Trans parent="li">Call centers (close sales directly during a call)</Trans>
              <Trans parent="li">
                Chatbots (include payment links into your bot's answers flow)
              </Trans>
              <Trans parent="li">
                <InternalPageLink slug="monei-pay/retail-POS-system">Retail</InternalPageLink>{' '}
                (small shops and pop-up stores)
              </Trans>
              <Trans parent="li">Wholesaling (include a payment link on purchase orders)</Trans>
              <Trans parent="li">NGOs: charge in real-time in a secure way</Trans>
              <Trans parent="li">
                Any business wanting to send special offers to specific contacts
              </Trans>
              <Trans parent="li">
                Holiday rentals (charge deposits for your bookings, without an intermediary)
              </Trans>
              <Trans parent="li">
                Partial payments (allow your clients to fraction their payments in a totally
                flexible way)
              </Trans>
            </List>
          </div>
        </Section>
      </Content>
      <ReviewsBackground>
        <div style={{display: 'flex', justifyContent: 'center', width: '100%'}}>
          <SectionHeader underline sx={{textAlign: 'center'}}>
            <Trans>What MONEI merchants are saying</Trans>
          </SectionHeader>
        </div>
        <GoogleReviews place={place} />
      </ReviewsBackground>
      <Content>
        <Section centered column>
          <FaqsSection faqs={faqs} title={<Trans>Pay By Link FAQ</Trans>} />
        </Section>
      </Content>
      <Content>
        <Section textAlign="center">
          <div>
            <SectionHeader underline tagName="h3">
              <Trans>Proudly used by more than 5000 stores</Trans>
            </SectionHeader>
            <Clients />
          </div>
        </Section>
      </Content>
    </>
  );
};

export default PayByLink;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: {ns: {in: ["common", "pay-by-link"]}, language: {eq: $language}}) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    googlePlacesPlace {
      user_ratings_total
      rating
    }
    allGooglePlacesReview(sort: {fields: rating, order: DESC}, filter: {rating: {gt: 2}}) {
      nodes {
        id
        rating
        text
        relative_time_description
        profile_photo_url
        author_name
      }
    }
  }
`;
